import React from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { Building2 } from "lucide-react";
import { FormField, Input } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { useTranslation } from "react-i18next";

interface ForwarderSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
}

// Top-level forwarder block for ROAD shipments. SEA/AIR carry their forwarder
// inside the sea/air details cards (seaDetails.forwarder / airDetails.forwarder);
// ROAD has no details block, so its forwarder is stored top-level (forwarder.*).
// The HBL/HAWB number fields are sea/air-specific and intentionally omitted here.
export const ForwarderSection: React.FC<ForwarderSectionProps> = ({
  register,
  errors,
  readonly,
  getErrorMessage,
}) => {
  const { t } = useTranslation("shipments");
  const forwarderErrors = errors.forwarder;

  return (
    <SectionCard icon={Building2} title={t("forwarder.title")}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
        <div className="space-y-1">
          <FormField
            layout="inline"
            size="sm"
            label={t("forwarder.companyName")}
            error={getErrorMessage(forwarderErrors?.companyName)}
          >
            <Input
              {...register("forwarder.companyName")}
              error={!!forwarderErrors?.companyName}
              placeholder={t("forwarder.companyNamePlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("forwarder.contactPerson")}
            error={getErrorMessage(forwarderErrors?.contactPerson)}
          >
            <Input
              {...register("forwarder.contactPerson")}
              error={!!forwarderErrors?.contactPerson}
              placeholder={t("forwarder.contactPersonPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.email")}
            error={getErrorMessage(forwarderErrors?.email)}
          >
            <Input
              {...register("forwarder.email")}
              type="email"
              error={!!forwarderErrors?.email}
              placeholder={t("placeholders.email")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        </div>

        <div className="space-y-1">
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.phone")}
            error={getErrorMessage(forwarderErrors?.phone)}
          >
            <Input
              {...register("forwarder.phone")}
              error={!!forwarderErrors?.phone}
              placeholder={t("placeholders.phone")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("forwarder.reference")}
            error={getErrorMessage(forwarderErrors?.reference)}
          >
            <Input
              {...register("forwarder.reference")}
              error={!!forwarderErrors?.reference}
              placeholder={t("forwarder.referencePlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        </div>
      </div>
    </SectionCard>
  );
};

ForwarderSection.displayName = "ForwarderSection";

export default ForwarderSection;
